import { useEffect, useState } from "react";
import { PageHeader } from "@/components/primitives";
import { api } from "@/lib/api";
import { ChevronRight } from "lucide-react";

const MONTH_IMAGE = {
  January:   "https://images.unsplash.com/photo-1597848212624-a19eb35e2651?w=480&q=70&auto=format&fit=crop",
  February:  "https://images.unsplash.com/photo-1565586574504-1bb55b50b59e?w=480&q=70&auto=format&fit=crop",
  March:     "https://images.unsplash.com/photo-1487530811176-3780de880c2d?w=480&q=70&auto=format&fit=crop",
  April:     "https://images.unsplash.com/photo-1568478922665-69b1c79f51e7?w=480&q=70&auto=format&fit=crop",
  May:       "https://images.unsplash.com/photo-1582719188393-bb71ca45dbb9?w=480&q=70&auto=format&fit=crop",
  June:      "https://images.unsplash.com/photo-1517666004288-c2a01a05daff?w=480&q=70&auto=format&fit=crop",
  July:      "https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=480&q=70&auto=format&fit=crop",
  August:    "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=480&q=70&auto=format&fit=crop",
  September: "https://images.unsplash.com/photo-1551952238-2315fab59d77?w=480&q=70&auto=format&fit=crop",
  October:   "https://images.unsplash.com/photo-1597106776019-b4ecc878c202?w=480&q=70&auto=format&fit=crop",
  November:  "https://images.unsplash.com/photo-1583161288033-c97d11b35bd0?w=480&q=70&auto=format&fit=crop",
  December:  "https://images.unsplash.com/photo-1597848212624-0e8be9f0a3a1?w=480&q=70&auto=format&fit=crop",
};

export default function Almanack() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/almanack").then((r) => setEntries(r.data)).catch(() => {}); }, []);

  const currentMonth = new Date().toLocaleString("en-NZ", { month: "long" });

  return (
    <div data-testid="almanack-page">
      <PageHeader
        kicker="A Pocket Almanack"
        title="The Almanack"
        subtitle="Twelve months in the dahlia patch · tap any month for the keeper's notes"
      />

      <div className="space-y-3" data-testid="almanack-list">
        {entries.map((e) => {
          const active = currentMonth === e.month;
          const isOpen = open === e.month;
          return (
            <article
              key={e.month}
              className={`h-card overflow-hidden transition-shadow ${active ? "shadow-seal" : ""}`}
              data-testid={`almanack-${e.month.toLowerCase()}`}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : e.month)}
                className="w-full flex items-stretch gap-4 p-3 text-left hover:bg-cream-deep/30 transition-colors"
                data-testid={`almanack-toggle-${e.month.toLowerCase()}`}
              >
                <div className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-cream-deep">
                  <img
                    src={MONTH_IMAGE[e.month]}
                    alt={`${e.month} in the patch`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(ev) => { ev.currentTarget.style.opacity = 0.25; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="kicker text-[11px]">{e.month}</div>
                  <div className="font-display text-2xl sm:text-3xl text-sage-dark leading-tight mt-1 flex items-center gap-2 flex-wrap">
                    {e.kicker}
                    {active && <span className="text-xs bg-sage text-cream-soft px-2 py-0.5 rounded-full tracking-wider uppercase">Now</span>}
                  </div>
                  <p className="mt-1 text-ink-soft text-base sm:text-lg leading-snug line-clamp-2 whitespace-pre-line">
                    {e.body}
                  </p>
                </div>
                <ChevronRight
                  size={20}
                  className={`text-sage-dark self-center shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="px-4 sm:px-5 pb-5 -mt-1 animate-fade-up" data-testid={`almanack-body-${e.month.toLowerCase()}`}>
                  <div className="divider mb-3"><span>· {e.season} ·</span></div>
                  <p className="font-display text-lg sm:text-xl text-ink leading-relaxed whitespace-pre-line">
                    {e.body}
                  </p>
                </div>
              )}
            </article>
          );
        })}
      </div>

      <p className="text-center font-display italic text-ink-soft mt-10">
        The year turns. The patch remembers.
      </p>
    </div>
  );
}
